import React, { useState, useRef } from 'react';
import { Modal, Button, Form, ProgressBar } from 'react-bootstrap';
import { KTIcon } from '../../../../_metronic/helpers';
import { toast } from 'react-toastify';
import { uploadFileToWasabi, deleteFileFromWasabi, generateFileId } from '../services/wasabiUploadService';
import { upload_response } from '../api/assetsApi';

interface EditAssetFileModalProps {
  show: boolean;
  onHide: () => void;
  uploadId: string;
  oldFilePath: string | null;
  onSuccess?: () => void;
}

const EditAssetFileModal: React.FC<EditAssetFileModalProps> = ({
  show,
  onHide,
  uploadId,
  oldFilePath,
  onSuccess
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type - must be zip
      const fileName = file.name.toLowerCase();
      const fileExtension = fileName.substring(fileName.lastIndexOf('.'));
      const validExtensions = ['.zip'];
      const validMimeTypes = ['application/zip', 'application/x-zip-compressed', 'application/x-zip'];
      
      if (!validExtensions.includes(fileExtension) && !validMimeTypes.includes(file.type)) {
        toast.error('Please select a valid ZIP file.');
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        setSelectedFile(null);
        return;
      }
      
      setSelectedFile(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error('Please select a ZIP file to upload.');
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);

    try {
      // Step 1: Delete old Wasabi file if exists
      if (oldFilePath) {
        setUploadProgress(10);
        try {
          const deleteResult = await deleteFileFromWasabi(oldFilePath);
          if (deleteResult.success) {
            if (deleteResult.deleted) {
              console.log('✅ Old Wasabi file deleted successfully:', deleteResult.message);
              toast.success('Old file deleted from Wasabi. Uploading new file...');
            } else {
              console.log('ℹ️ Old file already deleted or not found:', deleteResult.message);
              toast.info('Old file not found in Wasabi (may have been already deleted). Uploading new file...');
            }
          } else {
            console.error('❌ Failed to delete old Wasabi file:', deleteResult.message);
            toast.warning('Could not delete old file from Wasabi, but proceeding with new upload...');
          }
        } catch (deleteError: any) {
          console.error('❌ Failed to delete old Wasabi file:', deleteError);
          toast.warning('Could not delete old file from Wasabi, but proceeding with new upload...');
        }
      }

      // Step 2: Upload new file to Wasabi
      setUploadProgress(20);
      const fileId = generateFileId(selectedFile.name, selectedFile.size);
      
      const { fileUrl } = await uploadFileToWasabi(
        selectedFile,
        (progressData) => {
          // Map Wasabi progress (20% to 90%)
          const wasabiProgress = 20 + (progressData.percentage * 0.7);
          setUploadProgress(wasabiProgress);
        },
        fileId
      );

      // Step 3: Call upload_response API with new file path
      setUploadProgress(95);
      await upload_response(uploadId, fileUrl);
      
      setUploadProgress(100);
      toast.success('File uploaded and replaced successfully!');
      
      // Reset form
      setSelectedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      
      // Call success callback and close modal
      if (onSuccess) {
        onSuccess();
      }
      onHide();
    } catch (error: any) {
      console.error('Upload error:', error);
      const errorMessage = error?.response?.data?.message || 
                          error?.response?.data?.error || 
                          error?.message || 
                          'Failed to upload file. Please try again.';
      toast.error(errorMessage);
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const handleClose = () => {
    if (!isUploading) {
      setSelectedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      setUploadProgress(0);
      onHide();
    }
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <KTIcon iconName="pencil" className="me-2" />
          Replace Asset File
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="mb-4">
          <p className="text-muted">
            Select a new ZIP file to replace the existing asset file. The old file will be automatically deleted from Wasabi.
          </p>
        </div>

        <Form.Group className="mb-4">
          <Form.Label className="required">ZIP File</Form.Label>
          <Form.Control
            ref={fileInputRef}
            type="file"
            accept=".zip,application/zip,application/x-zip-compressed"
            onChange={handleFileChange}
            disabled={isUploading}
          />
          {selectedFile && (
            <div className="mt-2">
              <small className="text-muted">
                Selected: <strong>{selectedFile.name}</strong> ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
              </small>
            </div>
          )}
        </Form.Group>

        {isUploading && (
          <div className="mb-3">
            <div className="d-flex justify-content-between mb-2">
              <small className="text-muted">Uploading...</small>
              <small className="text-muted">{Math.round(uploadProgress)}%</small>
            </div>
            <ProgressBar now={uploadProgress} animated striped />
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose} disabled={isUploading}>
          Cancel
        </Button>
        <Button 
          variant="primary" 
          onClick={handleUpload} 
          disabled={!selectedFile || isUploading}
        >
          {isUploading ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              Uploading...
            </>
          ) : (
            <>
              <KTIcon iconName="file-up" className="me-2" />
              Upload & Replace
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default EditAssetFileModal;
